import { useEffect, useState, lazy, Suspense } from "react";

import { Row } from "react-bootstrap";

import { StationStatus } from "utils/constants";
import "./InterferenceEventComponent.css";
import { useMarkerStatus } from "utils/hooks";
import { Broadcast } from "react-bootstrap-icons";

const MapComponent = lazy(() =>
  import("components").then((module) => ({ default: module.MapComponent }))
);

/**
 * InterferenceEventComponent displays the interference event status,
 * including a color-coded icon and a map centered on the tenant's country.
 *
 * @memberof module:components/hoc
 * @param {Object} props
 * @param {string} props.stationId - The ID of the station to observe for status.
 * @param {string} props.tenantCountry - The tenant country code (e.g., "RO", "AU") to center the map.
 * @param {Array<number>} props.stationCoords - Coordinates of the station [longitude, latitude].
 *
 * @returns {JSX.Element} The rendered interference event component.
 */
export const InterferenceEventComponent = (props) => {
  const { connectionStatusHistory, warning, status } = useMarkerStatus(
    props.stationId,
    null,
    () => {}
  );
  var countries = [
    { name: "RO", center: [25.0094303, 45.9442858] },
    { name: "AU", center: [14.5162, 47.5501] },
  ];
  const [tenantCountry, setTenantCountry] = useState(countries[0]);
  const [iconClass, setIconClass] = useState("blink");
  const [color, setColor] = useState("#ffA500");

  useEffect(() => {
    if (props.tenantCountry === "RO") setTenantCountry(countries[0]);

    if (props.tenantCountry === "AU") setTenantCountry(countries[1]);
  }, [props.tenantCountry]);

  useEffect(() => {
    switch (status.code) {
      case StationStatus.state.CONNECTING:
      case StationStatus.state.DISCONNECTED:
        setColor("grey");
        setIconClass("blink");
        break;
      case StationStatus.state.CONNECTED:
      case StationStatus.state.SPECTRUM_CLEAN:
        setColor("#198754");
        setIconClass("blink");
        break;
      case StationStatus.state.INTERFERENCE_DETECTED:
      case StationStatus.state.SPOOFING_DETECTED:
      case StationStatus.state.SPOOFING_AND_INTERFERENCE_DETECTED:
        setColor("#bf1f27");
        setIconClass("blink");
        break;
      case StationStatus.state.DATA_UNAVAILABLE:
        setColor("#ffA500");
        setIconClass("blink");
        break;
      default:
        setColor("");
    }

    props.onStatusChanged && props.onStatusChanged(status,warning)
  }, [status.code, warning.code]);

  return (
    <>
      <span
        style={{
          display: "inline-block",
          height: "2.5em",
          lineHeight: "1em",
          whiteSpace: "normal",
          marginTop: "1em",
        }}
      >
        <Broadcast className={iconClass} style={{ color: color }}></Broadcast>
        <i style={{ color: color }}>
          {status.code === StationStatus.state.DATA_UNAVAILABLE
            ? " " + warning.message
            : " " + status.message}
        </i>
      </span>
      <Row>
        {tenantCountry && (
          <Suspense fallback={<div>Loading map...</div>}>
            <MapComponent
              markerColor={color}
              country={tenantCountry.name}
              width={200}
              height={200}
              stationCoords={props.stationCoords}
            />
          </Suspense>
        )}
      </Row>
    </>
  );
};
